import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { always, assoc, compose, cond, equals, find, flatten, map, prop, propOr, T, isNil, clone, dissoc, concat, pick, mapObjIndexed } from 'ramda';
import { isArray, isNilOrEmpty } from 'ramda-adjunct';
import { splitPagesByBreak } from '../docs/components/helpers/pages';
import FullscreenLoading from '../../components/FullscreenLoading';
import useFetchQuery from '../../hooks/useFetchQuery';
import PreviewDocumentHtmlMutation from '../../_graphql/mutations/documents/PreviewDocumentHtmlMutation';
import { errorMessage, successMessage } from '../../utils/messageMutation';
import { getClassNameOrientation } from '../../utils/getClassNameOrientation';
import { BASE_PADDING_DOCUMENT, DEFAULT_HEIGHT_PAGE_LANDSCAPE, DEFAULT_HEIGHT_PAGE_PORTRAIT } from '../../_CONST';
import { getEnvironment } from '../../_graphql/Environment';
import { QDocumentInstanceQuery } from '../../_graphql/queries/documentInstances/QDocumentInstance';
import UpdateDocumentInstanceMutation from '../../_graphql/mutations/documentInstances/UpdateDocumentInstanceMutation';
import CreateWidgetMutation from '../../_graphql/mutations/documents/CreateWidgetMutation';
import useTokenValidation from '../../hooks/useTokenValidation';
import { normalizeVariables } from '../helpers/normalizeVariables';
import PreviewDocument from './components/document/PreviewDocument';
import RepriseWidgetModal from './components/document/RepriseWidgetModal';

const BASE_WIDTH = 1160;
const BASE_HEIGHT = 900;

const RepriseDocument = () => {
  const location = useLocation();
  const { isValid, token, documentId, externalId } = useTokenValidation(location);

  const [env, setEnv] = useState(null);

  const [isLoadingHtml, setIsLoadingHtml] = useState(false);
  const [previewHtml, setPreviewHtml] = useState(undefined);
  const [classNameOrientationDocument, setClassNameOrientationDocument] = useState('');
  const [pageHeightDocument, setPageHeightDocument] = useState(0);
  const [widgetIdEditing, setWidgetIdEditing] = useState(null);
  const [scale, setScale] = useState(1);
  const shouldSkip = isNilOrEmpty(env);

  const { documentInstance, isLoading, reFetch } = useFetchQuery({
    query: QDocumentInstanceQuery,
    dataProp: 'documentInstance',
    defaultData: {},
    args: { externalId, documentId },
    env,
    skip: shouldSkip
  });

  const previewDocumentHtml = () => {
    setIsLoadingHtml(true);

    const normalizeWidgetsDeep = (obj) => {
      if (isArray(obj)) {
        return map(normalizeWidgetsDeep, obj);
      }
      if (obj && typeof obj === 'object') {
        return mapObjIndexed(normalizeWidgetsDeep, dissoc('rootWidgetId', obj));
      }
      return obj;
    };

    const normalizedInstance = normalizeWidgetsDeep(clone(documentInstance));
    const normalizedVariables = normalizeVariables(propOr({}, 'variables', documentInstance));

    PreviewDocumentHtmlMutation(
      {
        pages: propOr([], 'pages', normalizedInstance),
        variables: normalizedVariables,
        documentId,
        padding: propOr(BASE_PADDING_DOCUMENT, 'padding', documentInstance),
        orientation: propOr('portrait', 'orientation', documentInstance),
        renderingEngineVersion: propOr('v2', 'renderingEngineVersion', documentInstance),
        isVariablesHighlighted: false,
        teamId: prop('teamId', documentInstance),
        isLateEdit: true
      },
      (ok, error, html) => {
        setIsLoadingHtml(false);

        if (ok && !error) {
          const modifiedHtml = splitPagesByBreak(html);
          setPreviewHtml(modifiedHtml);

          setClassNameOrientationDocument(getClassNameOrientation(propOr('orientation', documentInstance)));

          setPageHeightDocument(cond([
            [equals('portrait'), always(DEFAULT_HEIGHT_PAGE_PORTRAIT)],
            [equals('landscape'), always(DEFAULT_HEIGHT_PAGE_LANDSCAPE)],
            [T, always(DEFAULT_HEIGHT_PAGE_PORTRAIT)]
          ])(propOr('orientation', documentInstance)));
        } else {
          errorMessage();
        }
      }
    );
  };

  useEffect(() => {
    if (token) getEnvironment(token).then(setEnv);
  }, [token]);

  useEffect(() => {
    function handleResize () {
      const scaleX = window.innerWidth / BASE_WIDTH;
      const scaleY = window.innerHeight / BASE_HEIGHT;
      setScale(Math.min(scaleX, scaleY));
    }
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (isValid && !isLoading && documentInstance) {
      previewDocumentHtml();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isValid, isLoading, documentInstance]);

  const updateWidget = (values, removeLoading) => {
    const widgetId = prop('id', values);

    values = pick([
      'name',
      'nbColsGrid',
      'heightRowsGrid',
      'contents',
      'scope',
      'offerId',
      'orientation',
      'frameStyle',
      'hasReplacedVariables',
      'isLateEdit'
    ], values);

    const getAllSections = page =>
      concat(concat(propOr([], 'widgets', page), propOr([], 'header', page)), propOr([], 'footer', page));

    const widgetToUpdate = compose(
      find(widget => equals(widgetId, prop('widgetId', widget)) || equals(widgetId, prop('rootWidgetId', widget))),
      flatten,
      map(section => propOr([], 'widgets', section)),
      flatten,
      map(getAllSections)
    )(propOr([], 'pages', documentInstance));

    const handleWidgetUpdateResult = (ok, error) => {
      if (ok && !error) {
        successMessage('widget', 'mis à jour');
        reFetch();
        setWidgetIdEditing(null);
      } else {
        errorMessage();
      }
    };

    if (isNil(widgetToUpdate)) {
      removeLoading();
      errorMessage();
      return;
    }

    // After a first edit the clicked id is the late-edit copy: always copy from the original widget.
    const rootWidgetId = propOr(widgetId, 'rootWidgetId', widgetToUpdate);

    CreateWidgetMutation({
      widget: {
        ...values, rootWidgetId, isLateEdit: true
      }
    }, (ok, error, newWidgetId) => {
      removeLoading();
      if (ok && newWidgetId && !error) {
        const updateSectionWidgets = sections =>
          map(
            section =>
              assoc(
                'widgets',
                map(
                  widget =>
                    equals(rootWidgetId, prop('rootWidgetId', widget))
                      ? assoc('widgetId', newWidgetId, widget)
                      : widget,
                  propOr([], 'widgets', section)
                ),
                section
              ),
            sections
          );

        const pages = compose(
          map(page => ({
            ...page,
            widgets: updateSectionWidgets(propOr([], 'widgets', page)),
            header: updateSectionWidgets(propOr([], 'header', page)),
            footer: updateSectionWidgets(propOr([], 'footer', page))
          })),
          prop('pages')
        )(documentInstance);

        UpdateDocumentInstanceMutation({ id: prop('id', documentInstance), pages }, (ok, error) => handleWidgetUpdateResult(ok, error));
      } else {
        errorMessage();
      }
    }, env);
  };

  if (equals(isValid, false)) {
    return (
      <div className="text-center mt-12 text-flexi-red font-bold">
        Accès refusé
      </div>
    );
  }

  const isLoadingPreview = isLoadingHtml || isNil(isValid) || isLoading || shouldSkip;

  if (isLoadingPreview) {
    return <FullscreenLoading />;
  }

  return (
    <div
      className="overflow-hidden absolute left-1/2 top-0 m-0 p-0"
      // eslint-disable-next-line no-restricted-syntax
      style={{
        width: `${BASE_WIDTH}px`,
        minHeight: `${BASE_HEIGHT}px`,
        transform: `translate(-${(BASE_WIDTH * scale) / 2}px, 0) scale(${scale})`,
        transformOrigin: 'top left'
      }}
    >
      <PreviewDocument
        hasToBeOnSameSection
        pagesHtml={previewHtml}
        classNameOrientationDocument={classNameOrientationDocument}
        activeKey={0}
        previewHtmlLoading={isLoadingHtml}
        elementsToAdd={<span className="text-xl uppercase text-flexi-text font-bold">{prop('name', documentInstance)}</span>}
        pageHeightDocument={pageHeightDocument}
        previewDocumentHtml={previewDocumentHtml}
        onWidgetEdit={setWidgetIdEditing}
      />
      {widgetIdEditing && (
        <RepriseWidgetModal
          widgetIdEditing={widgetIdEditing}
          onClose={() => setWidgetIdEditing(null)}
          updateWidget={updateWidget}
          teamId={prop('teamId', documentInstance)}
          variables={prop('variables', documentInstance)}
          scale={scale}
          env={env}
          offerId={prop('offerId', documentInstance) || null}
        />
      )}
    </div>
  );
};

export default RepriseDocument;
